import moment from 'moment';

import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import {
  BundledFeatureCategory,
  IOnboardingDiscount,
  IOnboardingDiscounts, IOnboardingLink,
  IOnboardingSaga,
  OnboardingCountryInterface,
  SignupPlan, SignupPlanBillingPeriodDetail
} from '@shared/interfaces/onboarding-saga.interface';
import { SharedModule } from '@shared/shared.module';

import { SignupComponentStore } from '../signup.component.store';
import { AppConstants } from '@core/constants';

const PERIODS = [{
  id: 1,
  name: "Annual",
  slug: "annual"
}, {
  id: 2,
  name: "Quarterly",
  value: "quarter"
}, {
  id: 3,
  name: "Monthly",
  value: "month"
}]

@Component({
  selector: 'app-signup-plan',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './signup-plan.component.html',
  styleUrls: ['./signup-plan.component.scss']
})
export class SignupPlanComponent implements OnInit {
  @Input() selectedCountry?: number;
  @Input() selectedPeriod: number = 1;

  private readonly store = inject(SignupComponentStore);
  saga?: IOnboardingSaga;
  countries?: OnboardingCountryInterface[] | null = []

  ngOnInit(): void {
    this.setupListeners();
  }

  get plan(): SignupPlan {
    const plan = this.saga?.plan;
    if (!plan) throw new Error('Invalid plan');
    return plan;
  }

  get link(): IOnboardingLink | undefined {
    return this.saga?.link;
  }

  get discountPrices(): IOnboardingDiscounts | undefined {
    return this.saga?.discount_prices;
  }

  get discountInfo(): IOnboardingDiscount {
    return this.hasDiscount()
  }

  public hasBillingPeriodDetails(periodType: number | undefined = undefined): boolean {
    const billingPeriodDetails = this.getBillingPeriodDetails();
    if (billingPeriodDetails.length === 0) {
      return false
    }

    if (!periodType) {
      return billingPeriodDetails.length > 0;
    }

    return billingPeriodDetails.some(detail => detail.period_type === periodType);
  }

  public getBillingPeriodDetails(): Array<SignupPlanBillingPeriodDetail> {
    const billingPeriodDetails = (this.plan?.billing_period_details ?? []).filter(d => d.renewal_unit === 1);
    if (!billingPeriodDetails) {
      return []
    }
    
    return billingPeriodDetails;
  }

  public getRenewalPeriodText(periodType: number | undefined = undefined): string | boolean {
    const period = this.getRenewalPeriod(periodType);
    if (!period) {
      return false;
    }
    return period?.name ? period.name : false;
  }

  public getStartDate(): string {
    return this.getStartDateMoment().format("MMMM Do, YYYY");
  }

  public getNextRenewalDate(periodType: number | undefined = undefined): string | boolean {
    const period = this.getRenewalPeriod(periodType);
    if (!period) {
      return false;
    }

    const startDateMoment = this.getStartDateMoment();
    switch (period.id) {
      case 1: {
        return startDateMoment.clone().add(1, 'years').format("MMMM Do");
      }
      case 2: {
        return startDateMoment.clone().add(3, 'months').format("MMMM Do");
      }
      case 3: {
        return startDateMoment.clone().add(1, 'months').format("MMMM Do");
      }
      default: {
        console.warn("Invalid period specified");
        return "";
      }
    }
  }

  public getMinimumContractDuration(periodType: number | undefined = undefined): number | boolean {
    if (!periodType) {
      return this.plan.minimum_contract_duration ?? false;
    }

    const billingPeriodDuration = this.getBillingPeriodByType(periodType)?.minimum_contract_duration;
    return billingPeriodDuration ? billingPeriodDuration : false;
  }

  public getCancellationNoticeTime(periodType: number | undefined = undefined): number | boolean {
    if (!periodType) {
      return this.plan.cancel_notice_time ?? false;
    }
    const billingPeriodCancelNotice = this.getBillingPeriodByType(periodType)?.cancel_notice_time;
    return billingPeriodCancelNotice ? billingPeriodCancelNotice : false;
  }

  public getEarliestCancellationDate(periodType: number | undefined = undefined): string | boolean {
    const billingPeriodDuration = this.getMinimumContractDuration(periodType);
    if (!billingPeriodDuration) {
      return false;
    }
    return moment()
      .add(billingPeriodDuration as number, "months")
      .format("MMMM Do, YYYY");
  }

  public getLatestCancellationNotice(periodType: number | undefined = undefined): string | boolean {
    const billingPeriodDuration = this.getMinimumContractDuration(periodType);
    const cancellationNoticeTime = this.getCancellationNoticeTime(periodType);
    if (!billingPeriodDuration || !cancellationNoticeTime) {
      return false;
    }
    return moment()
      .add(billingPeriodDuration as number, "months")
      .subtract(cancellationNoticeTime as number, "days")
      .format("MMMM Do, YYYY");
  }

  public showPlanPricing(): boolean {
    const dataLoaded = this.countries != null && this.selectedCountry != null && this.selectedPeriod != null;
    if (!dataLoaded) {
      return false
    }

    const country = (this.countries ?? []).find(c => c.id === this.selectedCountry);
    if (!country) {
      return false
    }

    const planPrice = this.plan.plan_prices?.find(p => p.region === country.region);
    if (!planPrice) {
      return false
    }

    return true
  }

  public getPlanPricing() {
    if (!this.showPlanPricing()) {
      return []
    }

    const country = (this.countries ?? []).find(c => c.id === this.selectedCountry)!;
    const planPrice = this.plan.plan_prices?.find(p => p.region === country.region)!;
    const planDiscounts = this.discountPrices;
    const regionDiscounts = (planDiscounts ?? {})[country.region];

    const planPricing: Record<string, any>[] = [];
    if (planPrice?.annual) { 
      let monthlySaving: string = '';
      if (planPrice?.month != null && planPrice?.annual != null && planPrice.month >= 1) {
        monthlySaving = (((planPrice.month - (planPrice?.annual / 12)) / planPrice.month) * 100).toFixed(0);
      }
      const planPricingDetail: Record<string, any> = {
        id: "annual",
        name: "Annual",
        period: "annually",
        price: planPrice?.annual.toFixed(2).replace('.00', ''),
        month_price: (planPrice?.annual / 12).toFixed(2),
        currency: country.currency,
        selected: this.selectedPeriod === 1,
        discount_price: (regionDiscounts ?? []).find(d => d.period === 1),
        monthlySaving,
      };
      const discountPriceDetail = (regionDiscounts ?? []).find(d => d.period === 1);
      if (discountPriceDetail != null && this.discountInfo?.type === 'percent') {
        const discountedPrice = Math.max(planPrice?.annual - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
        planPricingDetail.saved_price = (discountPriceDetail.discount_price / 12).toFixed(2);
      } else if (discountPriceDetail != null && this.discountInfo?.type === 'total') {
        const discountedPrice = Math.max(planPrice?.annual - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
      }
      planPricing.push(planPricingDetail);
    }
    if (planPrice?.quarter) {
      let monthlySaving: string = '';
      if (planPrice?.month != null && planPrice?.annual != null && planPrice.month >= 1) {
        monthlySaving = (((planPrice.month - (planPrice?.quarter / 3)) / planPrice.month) * 100).toFixed(0);
      }
      const planPricingDetail: Record<string, any> = {
        id: "quarter",
        name: "Quarterly",
        period: "quarterly",
        price: planPrice?.quarter.toFixed(2).replace('.00', ''),
        month_price: (planPrice?.quarter / 3).toFixed(2),
        currency: country.currency,
        selected: this.selectedPeriod === 2,
        discount_price: (regionDiscounts ?? []).find(d => d.period === 2),
        monthlySaving,
      };
      const discountPriceDetail = (regionDiscounts ?? []).find(d => d.period === 2);
      if (discountPriceDetail != null && this.discountInfo?.type === 'percent') {
        const discountedPrice = Math.max(planPrice?.quarter - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
        planPricingDetail.saved_price = (discountPriceDetail.discount_price / 3).toFixed(2);
      } else if (discountPriceDetail != null && this.discountInfo?.type === 'total') {
        const discountedPrice = Math.max(planPrice?.quarter - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
      }
      planPricing.push(planPricingDetail);
    }
    if (planPrice?.month) {
      const planPricingDetail: Record<string, any> = {
        id: "month",
        name: "Monthly",
        period: "monthly",
        price: planPrice?.month.toFixed(2).replace('.00', ''),
        month_price: planPrice?.month.toFixed(2),
        currency: country.currency,
        selected: this.selectedPeriod === 3,
      };
      const discountPriceDetail = (regionDiscounts ?? []).find(d => d.period === 3);
      if (discountPriceDetail != null && this.discountInfo?.type === 'percent') {
        const discountedPrice = Math.max(discountPriceDetail.month_price - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
        planPricingDetail.saved_price = discountPriceDetail.discount_price.toFixed(2);
      } else if (discountPriceDetail != null && this.discountInfo?.type === 'total') {
        const discountedPrice = Math.max(planPrice?.month - discountPriceDetail.discount_price, 0);
        planPricingDetail.discounted_price = Math.abs(discountedPrice).toFixed(2).replace('.00', '');
      }
      planPricing.push(planPricingDetail);
    }

    return planPricing;
  }

  public showDiscountInfo(): boolean {
    return Boolean(this.discountInfo?.value);
  }

  public getDiscountInfo(): string {
    const planPrice = this.getPlanPricing()[0];
    const currency = planPrice.currency ?? '';
    if (this.discountInfo?.type === 'percent') {
      return `A discount of ${this.discountInfo.value}% has been applied to this
      purchase. This discount is valid for the next ${this.discountInfo.apply_for_days} days. ${this.percentDiscountDates()}`
    } else {
      return `A credit of ${currency} ${this.discountInfo?.value} has been applied to your
      organization. This credit will expire in ${this.discountInfo?.apply_for_days}
      days. During this period, regular payments for this subscription will be
      deducted from the credit first, until it runs out. ${this.totalDiscountDates()}`
    }
  }

  isTaxableCountry(): boolean {
    return this.selectedCountry === 170;
  }

  getDiscountEndDate(planPrice: Record<string, any>): string {
    if (this.discountInfo?.type === 'percent') {
      return this.getPercentDiscountEndDate()
    } else {
      return this.getTotalDiscountEndDate(planPrice)
    }
  }

  private getPercentDiscountEndDate(): string {
    const today = moment();
    const discountEnd = today.clone().add(this.discountInfo?.apply_for_days!, 'days');
    return discountEnd.format(AppConstants.DATE_FORMAT.APP_FULL_DATE_MOMENT)
  }

  private getTotalDiscountEndDate(planPrice: Record<string, any>): string {
    const today = moment();
    const discountEnd = today.clone().add(this.discountInfo?.apply_for_days!, 'days');

    let totalDiscountLeft = this.discountInfo?.value || 0;
    let momentPeriodCount = 1;
    let momentPeriod = 'months';
    switch (planPrice.id) {
      case "annual":
        momentPeriod = 'years';
        break;
      case "quarter":
        momentPeriod = 'months';
        momentPeriodCount = 3;
        break;
      default:
        break;
    }

    let nextDiscountDate = today.clone();
    const checkNextDiscountDate = today.clone().add(momentPeriodCount, momentPeriod as unknown as any);
    if (checkNextDiscountDate.isAfter(discountEnd, 'day')) {
      return discountEnd.format(AppConstants.DATE_FORMAT.APP_FULL_DATE_MOMENT);
    }

    while (nextDiscountDate < discountEnd && totalDiscountLeft > 0) {
      totalDiscountLeft = totalDiscountLeft - planPrice.price;
      nextDiscountDate.add(momentPeriodCount, momentPeriod as unknown as any);
    }

    if (nextDiscountDate < discountEnd) {
      return nextDiscountDate.format(AppConstants.DATE_FORMAT.APP_FULL_DATE_MOMENT)
    } else {
      return discountEnd.format(AppConstants.DATE_FORMAT.APP_FULL_DATE_MOMENT);
    }
  }

  getDiscountValue(): string {
    if (this.discountInfo?.type === 'percent') {
      return `${this.discountInfo.value}% off for ${this.discountInfo?.apply_for_days} days`
    } else {
      const planPrice = this.getPlanPricing()[0];
      const currency = planPrice.currency ?? '';
      return `${currency} ${this.discountInfo?.value} off`;
    }
  }

  private totalDiscountDates(): string {
    let discountText = '';
    const today = moment();
    const selectedPrice = this.getPlanPricing().filter(p => p.selected)[0];
    const discountEnd = today.clone().add(this.discountInfo?.apply_for_days!, 'days');
    let totalDiscountLeft = this.discountInfo?.value || 0;

    let momentPeriodCount = 1;
    let momentPeriod = 'months';
    switch (selectedPrice.id) {
      case "annual":
        momentPeriod = 'years';
        break;
      case "quarter":
        momentPeriod = 'months';
        momentPeriodCount = 3;
        break;
      default:
        break;
    }

    let latestDiscountDate = today.clone();
    let intermediateDiscountDate = today.clone();
    let nextDiscountDate = today.clone();
    let intermediatePrice = selectedPrice.month_price;

    const checkNextDiscountDate = today.clone().add(momentPeriodCount, momentPeriod as unknown as any);
    if (checkNextDiscountDate.isAfter(discountEnd, 'day')) {
      const currentPrice = Math.max(selectedPrice.price - totalDiscountLeft, 0);
      discountText = `For now, you pay ${selectedPrice.currency} ${Math.abs(currentPrice).toFixed(2)}.`;
      discountText += ` From ${checkNextDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${selectedPrice.price.toFixed(2)}`;
      return discountText;
    }

    while (nextDiscountDate < discountEnd && totalDiscountLeft > 0) {
      totalDiscountLeft = totalDiscountLeft - selectedPrice.price;

      if (totalDiscountLeft < 0.01) {
        intermediateDiscountDate = nextDiscountDate.clone();
        intermediatePrice = Math.abs(totalDiscountLeft).toFixed(2)
      } else {
        intermediateDiscountDate = nextDiscountDate.clone();
        latestDiscountDate = nextDiscountDate.clone();
      }
      nextDiscountDate.add(momentPeriodCount, momentPeriod as unknown as any);
    }

    if (intermediatePrice < 0.01) {
      if (latestDiscountDate.isSame(today, 'day')) {
        discountText = `For now, you pay ${selectedPrice.currency} 0.`;
      } else {
        discountText = `Until ${intermediateDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} 0.00.`;
      }
      discountText += ` From ${nextDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${selectedPrice.price.toFixed(2)}`;
      return discountText;
    }

    if (latestDiscountDate.isSame(today, 'day')) {
      if (intermediateDiscountDate.isSame(latestDiscountDate, 'day')) {
        discountText = `For now, you pay ${selectedPrice.currency} ${intermediatePrice}.`;
      } else {
        discountText = `For now, you pay ${selectedPrice.currency} 0.`;
        discountText += ` On ${intermediateDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${intermediatePrice}`;
      }
    } else {
      discountText = `From now until ${latestDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} 0.00.`;
      if (!intermediateDiscountDate.isSame(latestDiscountDate, 'day')) {
        discountText += ` On ${intermediateDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${intermediatePrice}`;
      }
    }

    discountText += ` From ${nextDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${selectedPrice.price.toFixed(2)}`;
    return discountText;
  }

  private percentDiscountDates(): string {
    const selectedPrice = this.getPlanPricing().filter(p => p.selected)[0];
    const today = moment();
    const discountEnd = today.clone().add(this.discountInfo?.apply_for_days!, 'days');

    let momentPeriodCount = 1;
    let momentPeriod = 'months';
    switch (selectedPrice.id) {
      case "annual":
        momentPeriod = 'years';
        break;
      case "quarter":
        momentPeriod = 'months';
        momentPeriodCount = 3;
        break;
      default:
        break;
    }

    let latestDiscountDate = today.clone();
    let nextDiscountDate = today.clone();

    while (nextDiscountDate < discountEnd) {
      latestDiscountDate = nextDiscountDate.clone();
      nextDiscountDate.add(momentPeriodCount, momentPeriod as unknown as any);
    }

    let discountText = '';
    if (latestDiscountDate.isSame(today, 'day')) {
      discountText = `For now, you pay ${selectedPrice.currency} ${selectedPrice.discounted_price}.`;
    } else {
      discountText = `Until ${latestDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${selectedPrice.discounted_price}.`;
    }

    return discountText += ` From ${nextDiscountDate.format("MMMM Do, YYYY")} you pay ${selectedPrice.currency} ${selectedPrice.month_price}`;
  }

  public bundledFeatures(): string[] | null {
    if (!this.plan.bundled_features) {
      return null
    }

    const bundledFeatures = this.plan.bundled_features.filter(f => {
      return f.category !== BundledFeatureCategory.SPORTS && !['lactate', 'ppd'].includes(f.slug)
    }).map(f => f.name);

    if (!bundledFeatures.length) {
      return null
    }

    return bundledFeatures;
  }

  private getStartDateMoment(): moment.Moment {
    if (this.link?.plan_start_date) {
      return moment(this.link.plan_start_date);
    }

    return moment();
  }

  private getRenewalPeriod(periodType: number | undefined = undefined): Record<string, any> | undefined {
    let period;
    const renewalPeriodUnit = this.selectedPeriod ?? this.plan.renewal_unit;
    if (!periodType) {
      period = PERIODS.find(period => period.id === renewalPeriodUnit);
    } else {
      period = PERIODS.find(period => period.id === periodType);
    }
    return period;
  }


  private getBillingPeriodByType(periodType: number): SignupPlanBillingPeriodDetail | undefined {
    const details = this.plan?.billing_period_details;
    if (!details) {
      return undefined
    }

    return details.find(detail => detail.period_type === periodType);
  }

  private hasDiscount(): IOnboardingDiscount {
    if (!this.saga) {
      return {}
    }

    return {
      type: this.link?.discount_type,
      apply_for_days: this.link?.apply_for_days,
      value: this.link?.discount_value,
    }
  }

  private setupListeners(): void {
    this.store.viewState.$.subscribe(viewState => {
      if (viewState.status === 'loaded') {
        this.saga = viewState.data.saga;
        this.countries = viewState.data.countries;
      }
    })
  }
}
